// Live's Move to project (#668, spec docs/specs/web/S1c-library-move-bulk.md): live's project
// picker over one file (its card's menu) or several (the bulk bar). The route moves at most 100
// files at once, all or nothing, so a larger selection moves in batches of 100, one after the
// other. If a batch is refused, the batches before it have moved and it and those after have
// not, and the dialog says so in those words. Then the brand's lists and Recent Projects are read
// again, whatever moved. A card dropped on a project tile moves the same way (`useMoveFiles`,
// spec library-card-drag).
import { Modal, Stack, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getListRecentFoldersQueryKey,
  moveAssets,
} from "../../api/generated/endpoints/assets/assets";
import { assetMoveAssetIdsMax } from "../../api/generated/zod";
import { refreshLibraryLists } from "../../api/library-lists";
import { ErrorAlert } from "../ErrorAlert";
import { FolderPicker } from "../folders/FolderPicker";

/** The files of one call, as the route takes them (`MAX_MOVED`). */
export const MOVE_BATCH = assetMoveAssetIdsMax;

/** A batch was refused: the files before it moved, it and the ones after did not. */
export class MoveBatchError extends Error {
  readonly moved: number;
  readonly total: number;
  override readonly cause: unknown;

  constructor(moved: number, total: number, cause: unknown) {
    super(`Moved ${moved} of ${total} files`);
    this.moved = moved;
    this.total = total;
    this.cause = cause;
  }
}

/** The ids in calls of at most `size`, in order (the route's `MAX_MOVED`, by default). */
export function batches(ids: readonly string[], size = MOVE_BATCH): string[][] {
  const out: string[][] = [];
  for (let start = 0; start < ids.length; start += size) out.push(ids.slice(start, start + size));
  return out;
}

/** What a refused move says: nothing moved, or which files did and which did not. */
export function movedWords(error: MoveBatchError): string | null {
  if (error.moved === 0) return null;
  const rest =
    error.total - error.moved === 1
      ? `File ${error.total} did not.`
      : `Files ${error.moved + 1} to ${error.total} did not.`;
  return `Files 1 to ${error.moved} moved. ${rest}`;
}

/** What a move takes: the files, and the folder they go into (null: the brand's top level). */
export interface FilesMove {
  assetIds: readonly string[];
  folderId: string | null;
}

/**
 * Moves files in calls of at most `batch`, one after the other; a refused one throws
 * `MoveBatchError`. Then the brand's lists and Recent Projects are read again, whatever moved.
 */
export function useMoveFiles(teamId: string, brandId: string, batch?: number) {
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      refreshLibraryLists(queryClient, teamId, brandId),
      queryClient.invalidateQueries({ queryKey: getListRecentFoldersQueryKey(teamId, brandId) }),
    ]);
  return useMutation({
    mutationFn: async ({ assetIds, folderId }: FilesMove) => {
      let moved = 0;
      for (const ids of batches(assetIds, batch)) {
        try {
          await moveAssets(teamId, brandId, { asset_ids: ids, folder_id: folderId });
        } catch (cause) {
          throw new MoveBatchError(moved, assetIds.length, cause);
        }
        moved += ids.length;
      }
    },
    onSuccess: refresh,
    // The batches before a refused one have moved: the lists show them where they are now.
    onError: refresh,
  });
}

export interface MoveToProjectProps {
  teamId: string;
  brandId: string;
  opened: boolean;
  assetIds: readonly string[];
  /**
   * The one file's own folder (null at the top level), chosen first with Move grey until another
   * is chosen; absent for several files, which start at "All assets & files" with Move enabled.
   */
  current?: string | null | undefined;
  onClose: () => void;
  /** After the files moved and the lists were read again. */
  onMoved?: (() => void) | undefined;
  /** The files of one call: the route's `MAX_MOVED` (a test moves a few in small batches). */
  batch?: number | undefined;
}

export function MoveToProject(props: MoveToProjectProps) {
  const { teamId, brandId, assetIds, current, onClose } = props;
  // Its own read of the lists runs first, so `onMoved` sees them read again.
  const move = useMoveFiles(teamId, brandId, props.batch);
  const close = () => {
    move.reset();
    onClose();
  };
  const failed = move.error instanceof MoveBatchError ? move.error : null;
  const words = failed === null ? null : movedWords(failed);
  return (
    <Modal opened={props.opened} onClose={close} title="Move to project" centered>
      {props.opened ? (
        <FolderPicker
          teamId={teamId}
          brandId={brandId}
          submitLabel="Move"
          initialId={current ?? null}
          initialIsCurrent={current !== undefined}
          pending={move.isPending}
          onSubmit={(folderId) =>
            move.mutate(
              { assetIds, folderId },
              {
                onSuccess: () => {
                  props.onMoved?.();
                  onClose();
                },
              },
            )
          }
          onCancel={close}
        />
      ) : null}
      {failed === null ? null : (
        <Stack gap={4} mt="sm">
          {words === null ? null : (
            <Text size="sm" component="output">
              {words}
            </Text>
          )}
          <ErrorAlert error={failed.cause} />
        </Stack>
      )}
    </Modal>
  );
}
